import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  collectionData,
  Timestamp,
  CollectionReference,
  DocumentReference,
} from '@angular/fire/firestore';
import { Observable, from, map } from 'rxjs';
import { User, CreateUserDto, Role, OnboardingStatus } from '../models/user.model';
import { VerificationSession } from '../models/verification.model';

/**
 * UserService
 * Handles all Firestore operations related to users
 * - CRUD operations on user documents
 * - User role and onboarding status management
 * - Admin queries for all users
 * - Verification sessions storage
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  private firestore: Firestore = inject(Firestore);
  private usersCollection: CollectionReference;

  constructor() {
    this.usersCollection = collection(this.firestore, 'users');
  }

  /**
   * Create a new user document in Firestore
   * Sets default values for onboarding and timestamps
   *
   * @param createUserDto - User data transfer object
   * @returns Promise that resolves when user is created
   */
  async createUser(createUserDto: CreateUserDto): Promise<void> {
    const userRef: DocumentReference = doc(this.usersCollection, createUserDto.uid);

    const userData: Omit<User, 'uid'> = {
      email: createUserDto.email,
      role: createUserDto.role || Role.USER,
      onboardingCompleted: createUserDto.onboardingCompleted || false,
      onboardingStatus: createUserDto.onboardingStatus || OnboardingStatus.PENDING,
      ...(createUserDto.diditWorkflowId !== undefined && { diditWorkflowId: createUserDto.diditWorkflowId }),
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await setDoc(userRef, userData);
  }

  /**
   * Get user document by UID
   *
   * @param uid - User's unique identifier
   * @returns Observable of User or null if not found
   */
  getUserById(uid: string): Observable<User | null> {
    const userRef = doc(this.usersCollection, uid);

    return from(getDoc(userRef)).pipe(
      map((docSnapshot) => {
        if (!docSnapshot.exists()) {
          return null;
        }

        const data = docSnapshot.data();
        return {
          uid: docSnapshot.id,
          ...data,
          // Convert Firestore Timestamps to Date objects
          createdAt: data['createdAt']?.toDate?.() || new Date(data['createdAt']),
          updatedAt: data['updatedAt']?.toDate?.() || new Date(data['updatedAt']),
        } as User;
      })
    );
  }

  /**
   * Get all users (Admin only)
   * Use this method only for admin dashboard
   * Firebase Security Rules should enforce admin-only access
   * ✅ Optimización: Limitar a 500 usuarios para evitar cargas masivas
   *
   * @returns Observable of User array
   */
  getAllUsers(limit: number = 500): Observable<User[]> {
    const q = query(this.usersCollection);
    return (collectionData(q, { idField: 'uid' }) as Observable<Record<string, any>[]>).pipe(
      map((docs) =>
        docs.map((data) => ({
          ...data,
          uid: data['uid'],
          createdAt: data['createdAt']?.toDate?.() || new Date(data['createdAt']),
          updatedAt: data['updatedAt']?.toDate?.() || new Date(data['updatedAt']),
        } as User))
      )
    );
  }

  /**
   * Get users filtered by onboarding status (Admin only)
   *
   * @param status - Onboarding status to filter by
   * @returns Observable of User array
   */
  getUsersByOnboardingStatus(status: OnboardingStatus): Observable<User[]> {
    const q = query(this.usersCollection, where('onboardingStatus', '==', status));
    return (collectionData(q, { idField: 'uid' }) as Observable<Record<string, any>[]>).pipe(
      map((docs) =>
        docs.map((data) => ({
          ...data,
          uid: data['uid'],
          createdAt: data['createdAt']?.toDate?.() || new Date(data['createdAt']),
          updatedAt: data['updatedAt']?.toDate?.() || new Date(data['updatedAt']),
        } as User))
      )
    );
  }

  /**
   * Update user's onboarding status
   *
   * @param uid - User's unique identifier
   * @param status - New onboarding status
   * @param diditWorkflowId - Optional workflow ID from Didit
   * @returns Promise that resolves when update is complete
   */
  async updateOnboardingStatus(
    uid: string,
    status: OnboardingStatus,
    diditWorkflowId?: string
  ): Promise<void> {
    const userRef = doc(this.usersCollection, uid);

    const updateData: any = {
      onboardingStatus: status,
      onboardingCompleted: status === OnboardingStatus.COMPLETED,
      updatedAt: new Date(),
    };

    if (diditWorkflowId) {
      updateData.diditWorkflowId = diditWorkflowId;
    }

    await updateDoc(userRef, updateData);
  }

  /**
   * Update user role (Admin only)
   *
   * @param uid - User's unique identifier
   * @param role - New role
   * @returns Promise that resolves when update is complete
   */
  async updateUserRole(uid: string, role: Role): Promise<void> {
    const userRef = doc(this.usersCollection, uid);

    await updateDoc(userRef, {
      role,
      updatedAt: new Date(),
    });
  }

  /**
   * Check if user is admin
   *
   * @param uid - User's unique identifier
   * @returns Observable of boolean
   */
  isAdmin(uid: string): Observable<boolean> {
    return this.getUserById(uid).pipe(map((user) => user?.role === Role.ADMIN));
  }

  /**
   * Delete a user document from Firestore
   *
   * @param uid - User's unique identifier
   * @returns Promise that resolves when user is deleted
   */
  async deleteUser(uid: string): Promise<void> {
    const userRef = doc(this.usersCollection, uid);
    await deleteDoc(userRef);
  }

  // ========================================
  // VERIFICATION SESSIONS MANAGEMENT
  // ========================================

  /**
   * Save verification session to Firestore
   * Stored at: users/{uid}/verifications/{sessionId}
   *
   * @param uid - User's unique identifier
   * @param session - Verification session data
   * @returns Promise that resolves when session is saved
   */
  async saveVerificationSession(uid: string, session: VerificationSession): Promise<void> {
    const sessionRef = doc(this.firestore, `users/${uid}/verifications/${session.sessionId}`);

    await setDoc(sessionRef, {
      ...session,
      createdAt: Timestamp.fromDate(session.createdAt.toDate()),
      updatedAt: Timestamp.fromDate(session.updatedAt.toDate()),
      finishedAt: session.finishedAt ? Timestamp.fromDate(session.finishedAt.toDate()) : null,
    });
  }

  /**
   * Update verification session in Firestore
   *
   * @param uid - User's unique identifier
   * @param sessionId - Session ID
   * @param updates - Partial session data to update
   * @returns Promise that resolves when session is updated
   */
  async updateVerificationSession(
    uid: string,
    sessionId: string,
    updates: Partial<VerificationSession>
  ): Promise<void> {
    const sessionRef = doc(this.firestore, `users/${uid}/verifications/${sessionId}`);

    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    if (updates.finishedAt) {
      updateData.finishedAt = Timestamp.fromDate(updates.finishedAt.toDate());
    }

    await updateDoc(sessionRef, updateData);
  }

  /**
   * Get verification session by ID
   *
   * @param uid - User's unique identifier
   * @param sessionId - Session ID
   * @returns Observable of VerificationSession or null
   */
  getVerificationSession(uid: string, sessionId: string): Observable<VerificationSession | null> {
    const sessionRef = doc(this.firestore, `users/${uid}/verifications/${sessionId}`);

    return from(getDoc(sessionRef)).pipe(
      map((docSnapshot) => {
        if (!docSnapshot.exists()) {
          return null;
        }

        const data = docSnapshot.data();
        return {
          ...data,
          createdAt: data['createdAt'],
          updatedAt: data['updatedAt'],
          finishedAt: data['finishedAt'] || null,
        } as VerificationSession;
      })
    );
  }

  /**
   * Get all verification sessions for a user
   *
   * @param uid - User's unique identifier
   * @returns Observable of VerificationSession array
   */
  getUserVerificationSessions(uid: string): Observable<VerificationSession[]> {
    const verificationsRef = collection(this.firestore, `users/${uid}/verifications`);

    return from(getDocs(verificationsRef)).pipe(
      map((querySnapshot) => {
        return querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            sessionId: doc.id,
          } as VerificationSession;
        });
      })
    );
  }
}
